"use client"

import { useState } from "react"
import { useDispatch } from "react-redux"
import { createCity, updateCity, deleteCity, getCitiesByState } from "../../slices/locationSlice"
import { toast } from "react-toastify"
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  Card,
  CardHeader,
  CardContent,
  Divider,
} from "@mui/material"
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material"

const CitiesTable = ({ cities, state }) => {
  const dispatch = useDispatch()
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [cityName, setCityName] = useState("")
  const [currentCity, setCurrentCity] = useState(null)

  const handleAddCity = async (e) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      await dispatch(createCity({ stateId: state._id, cityData: { name: cityName } })).unwrap()
      toast.success("City added successfully")
      setIsAddModalOpen(false)
      setCityName("")
      dispatch(getCitiesByState(state._id))
    } catch (error) {
      toast.error(error || "Failed to add city")
    } finally {
      setIsLoading(false)
    }
  }

  const handleEditCity = async (e) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      await dispatch(updateCity({ id: currentCity._id, cityData: { name: cityName } })).unwrap()
      toast.success("City updated successfully")
      setIsEditModalOpen(false)
      setCurrentCity(null)
      setCityName("")
      dispatch(getCitiesByState(state._id))
    } catch (error) {
      toast.error(error || "Failed to update city")
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeleteCity = async () => {
    setIsLoading(true)

    try {
      await dispatch(deleteCity(currentCity._id)).unwrap()
      toast.success("City deleted successfully")
      setIsDeleteModalOpen(false)
      setCurrentCity(null)
      // Explicitly fetch cities again after deletion
      dispatch(getCitiesByState(state._id))
    } catch (error) {
      toast.error(error || "Failed to delete city")
    } finally {
      setIsLoading(false)
    }
  }

  const openEditModal = (city) => {
    setCurrentCity(city)
    setCityName(city.name)
    setIsEditModalOpen(true)
  }

  const openDeleteModal = (city) => {
    setCurrentCity(city)
    setIsDeleteModalOpen(true)
  }

  return (
    <Card elevation={2}>
      <CardHeader
        title={`Cities in ${state.name}`}
        subheader="Manage cities for this state"
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setCityName("")
              setIsAddModalOpen(true)
            }}
          >
            Add City
          </Button>
        }
      />
      <Divider />
      <CardContent sx={{ p: 0 }}>
        <TableContainer component={Paper} elevation={0}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell width={80}>SR. NO.</TableCell>
                <TableCell>NAME</TableCell>
                <TableCell align="right">ACTIONS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cities.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No cities found for {state.name}. Add your first city!
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                cities.map((city, index) => (
                  <TableRow key={city._id} hover>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{city.name}</TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                        <Tooltip title="Edit">
                          <IconButton color="primary" onClick={() => openEditModal(city)} size="small">
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton color="error" onClick={() => openDeleteModal(city)} size="small">
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>

      {/* Add City Dialog */}
      <Dialog open={isAddModalOpen} onClose={() => !isLoading && setIsAddModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add City to {state.name}</DialogTitle>
        <form onSubmit={handleAddCity}>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Enter the name of the new city to add it to {state.name}.
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              id="cityName"
              label="City Name"
              type="text"
              fullWidth
              variant="outlined"
              value={cityName}
              onChange={(e) => setCityName(e.target.value)}
              required
              disabled={isLoading}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setIsAddModalOpen(false)} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isLoading || !cityName.trim()}
              startIcon={isLoading ? <CircularProgress size={20} /> : null}
            >
              {isLoading ? "Adding..." : "Add City"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Edit City Dialog */}
      <Dialog open={isEditModalOpen} onClose={() => !isLoading && setIsEditModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit City</DialogTitle>
        <form onSubmit={handleEditCity}>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>Update the name of the selected city.</DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              id="editCityName"
              label="City Name"
              type="text"
              fullWidth
              variant="outlined"
              value={cityName}
              onChange={(e) => setCityName(e.target.value)}
              required
              disabled={isLoading}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setIsEditModalOpen(false)} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isLoading || !cityName.trim()}
              startIcon={isLoading ? <CircularProgress size={20} /> : null}
            >
              {isLoading ? "Updating..." : "Update City"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete City Dialog */}
      <Dialog open={isDeleteModalOpen} onClose={() => !isLoading && setIsDeleteModalOpen(false)} maxWidth="sm">
        <DialogTitle>Delete City</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{currentCity?.name}</strong> from {state.name}?
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, color: "error.main" }}>This action cannot be undone.</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setIsDeleteModalOpen(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteCity}
            color="error"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {isLoading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  )
}

export default CitiesTable

